import { svgIconPaths } from '@/assets/svg/icons';
import CustomButton from '@/components/atoms/CustomButton';
import LoadingLogo from '@/components/atoms/LoadingLogo';
import ConfirmationModal from '@/components/molecules/ConfirmationModal';
import SidebarItemViewTemplate from '@/components/templates/SidebarItemViewTemplate';
import appRoutes from '@/constants/routes/app';
import useChangeProviderActivity from '@/hooks/providers/useChangeProviderActivity';
import usePatchProvider from '@/hooks/providers/usePatchProvider';
import useProviderById from '@/hooks/providers/useProviderById';
import ProviderForm from '@/modules/provider/ProviderForm';
import colors from '@/theme/colors';
import { TProviderApi } from '@/types/Provider.types';
import { HStack, useDisclosure, VStack } from '@chakra-ui/react';
import { createFileRoute, useNavigate } from '@tanstack/react-router';

import React from 'react';

const ProviderProfileView = () => {
  const { providerId } = Route.useParams();
  const navigate = useNavigate();

  const { isOpen, onClose, onOpen } = useDisclosure();

  const { isPending: isSubmitting, mutate } = usePatchProvider(providerId);
  const { isLoading, isSuccess, data, isPending, isRefetching, refetch: refetchProvider } = useProviderById(providerId);
  const { isPending: isChangingStatus, mutate: changeStatus } = useChangeProviderActivity(providerId);

  const showLoader = isLoading || isPending || isRefetching;

  return (
    <SidebarItemViewTemplate
      title="Provider Profile"
      buttonLabel={data?.isActive ? 'Deactivate Provider' : 'Activate Provider'}
      isButtonDisabled={showLoader}
      buttonColor={data?.isActive ? colors.extra.red : colors.extra.violet}
      onButtonClick={onOpen}
    >
      <HStack justify={'flex-start'} width={'100%'} marginTop={'20px'}>
        <CustomButton
          isTransparent
          iconBeforeLabelSvgPath={svgIconPaths.leftArrow}
          labelColor={colors.background.blueGray}
          labelHoverColor="extra.black"
          label="Back to Providers"
          hoverBackgroundColor="transparent"
          borderHoverColor="transparent"
          onClick={() => navigate({ to: appRoutes.providers })}
          labelStyle={{ marginBottom: '5px', fontSize: '16px' }}
        />
      </HStack>
      <VStack flex={1} width={'100%'} justifyContent={'center'} alignItems={'center'}>
        {showLoader && <LoadingLogo />}
        {!showLoader && isSuccess && (
          <ProviderForm
            defaultValues={data}
            providerId={providerId}
            submitLabel="Save Changes"
            onSubmit={(form, photo) => mutate({ form: form as TProviderApi, photo })}
            refetchProvider={refetchProvider}
            isPending={isSubmitting}
            isPhotoRequired={false}
          />
        )}
      </VStack>
      <ConfirmationModal
        isOpen={isOpen}
        title={data?.isActive ? 'Deactivate Provider' : 'Activate Provider'}
        description={`Are you sure you want to ${data?.isActive ? 'deactivate' : 'activate'} this orovider?`}
        onCancel={onClose}
        onConfirm={() => {
          changeStatus({ isActive: !data?.isActive });
          onClose();
        }}
        confirmLoading={isChangingStatus}
        confirmButtonText={data?.isActive ? 'Deactivate' : 'Activate'}
        confirmButtonColor={data?.isActive ? colors.extra.red : colors.extra.violet}
      />
    </SidebarItemViewTemplate>
  );
};

export default ProviderProfileView;

export const Route = createFileRoute('/_authenticated/_admin/providers/$providerId')({
  component: ProviderProfileView,
});
